import { ADMIN_EVENT_TYPES, type AdminEvent, type AdminEventType } from "./types";

export type RealtimeStatus = "connecting" | "open" | "reconnecting" | "offline" | "unsupported";

export interface RealtimeSubscription {
  onEvent: (event: AdminEvent) => void;
  onStatus: (status: RealtimeStatus) => void;
  /** After a reconnect the app refetches what's on screen (events may have been missed). */
  onResync: () => void;
}

export interface RealtimeSource {
  connect(sub: RealtimeSubscription): () => void;
}

/** For runtimes without an event stream (tests, stub transports): the UI falls back to polling. */
export const unsupportedRealtime: RealtimeSource = {
  connect(sub) {
    sub.onStatus("unsupported");
    return () => {};
  },
};

/**
 * EventSource wrapper for the admin stream (contract: streamAdminEvents): named events,
 * credentials, backoff reconnects, resync after reconnect, and clean teardown. Only the event
 * types passed in are subscribed.
 */
export function createEventSourceRealtime(url: string, eventTypes: readonly AdminEventType[] = ADMIN_EVENT_TYPES): RealtimeSource {
  return {
    connect(sub) {
      let source: EventSource | null = null;
      let timer: ReturnType<typeof setTimeout> | undefined;
      let attempt = 0;
      let opened = false;
      let disposed = false;
      const open = () => {
        if (disposed) return;
        sub.onStatus(opened ? "reconnecting" : "connecting");
        source = new EventSource(url, { withCredentials: true });
        source.onopen = () => {
          attempt = 0;
          sub.onStatus("open");
          if (opened) sub.onResync();
          opened = true;
        };
        for (const type of eventTypes) {
          source.addEventListener(type, (e) => {
            try {
              sub.onEvent(JSON.parse((e as MessageEvent<string>).data) as AdminEvent);
            } catch {
              /* ignore malformed events */
            }
          });
        }
        source.onerror = () => {
          if (!source || source.readyState !== EventSource.CLOSED) {
            sub.onStatus("reconnecting");
            return;
          }
          source.close();
          source = null;
          sub.onStatus(typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "reconnecting");
          timer = setTimeout(open, Math.min(30_000, 1000 * 2 ** attempt++));
        };
      };
      open();
      return () => {
        disposed = true;
        if (timer) clearTimeout(timer);
        source?.close();
      };
    },
  };
}
